import axios from "axios";
import type { Message } from "@ag-ui/client";
import { AguiRunControllerApi, AguiThreadControllerApi } from "@/lib/api/generated/casedb";
import type { AguiThreadSummary } from "@/lib/api/generated/casedb";
import { catalogApiClient } from "@/features/catalog_case_review/shared/services/catalogApiClient";
import { getOrCreateSessionId } from "@/features/catalog_case_review/shared/services/caseLockService";
import { PAGE_AGENT_NAME, RUN_IN_PROGRESS_CODE } from "../constants/pageAgentIds";
import type { AgentMessagesSnapshot } from "../model/agentThread";

/**
 * The caller's stored conversations with the page helper, clearing one, and the cancel of a run in
 * flight.
 *
 * Over the generated case-db client (`AguiThreadControllerApi`, `AguiRunControllerApi`), through the
 * same factory the catalog services use, so the base path and the authenticated axios are shared.
 */
const NOT_FOUND_STATUS = 404;
const SESSION_HEADER = "X-Session-Id";

function withSession(): { headers: Record<string, string> } {
  return { headers: { [SESSION_HEADER]: getOrCreateSessionId() } };
}

export class AgentThreadService {
  /** The caller's conversations with the page helper, newest activity first. */
  async threads(): Promise<AguiThreadSummary[]> {
    const client = await catalogApiClient(AguiThreadControllerApi);
    const response = await client.threads(PAGE_AGENT_NAME, withSession());
    return response.data;
  }

  /** One conversation's transcript, oldest first, unwrapped from the `MESSAGES_SNAPSHOT` event. */
  async messages(threadId: string): Promise<Message[]> {
    const client = await catalogApiClient(AguiThreadControllerApi);
    const response = await client.messages(PAGE_AGENT_NAME, threadId, withSession());
    // The route answers the event as a JSON string body; axios leaves a string body unparsed.
    const snapshot: AgentMessagesSnapshot = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
    return snapshot.messages;
  }

  /**
   * Clears a conversation: the server moves the thread's replay boundary so the model and this read
   * forget it. Nothing is deleted; the thread id stays valid for the next question.
   */
  async clearMessages(threadId: string): Promise<void> {
    const client = await catalogApiClient(AguiThreadControllerApi);
    await client.clearMessages(PAGE_AGENT_NAME, threadId, withSession());
  }

  /** Stops an in-flight run; the server ends the stream with `RUN_ERROR` / `RUN_CANCELLED`. */
  async cancelRun(runId: string): Promise<void> {
    const client = await catalogApiClient(AguiRunControllerApi);
    await client.cancel(runId, withSession());
  }
}

/** True when the thread routes are not deployed (a rolled-back Java side answers 404). */
export function isThreadRouteMissing(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === NOT_FOUND_STATUS;
}

/**
 * True when the server refused a clear because a run is running or queued on the thread. Matched on
 * the error code, not the status: a shared thread is also refused with 409 (`THREAD_NOT_CLEARABLE`).
 */
export function isRunInProgress(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const body: unknown = err.response?.data;
  return typeof body === "object" && body !== null && (body as { code?: unknown }).code === RUN_IN_PROGRESS_CODE;
}

export const agentThreadService = new AgentThreadService();
